import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

type ToastVariant = 'default' | 'success' | 'error' | 'info' | 'warning';

interface ShowToastProps {
  message: string;
  title?: string;
  icon?: ReactNode;
  onClick?: () => void;
  duration?: number;
  showCloseButton?: boolean;
  variant?: ToastVariant;
}

interface ToastItem extends ShowToastProps {
  id: string;
}

interface ToastContextType {
  showToast: (props: ShowToastProps) => string;
  toast: {
    dismiss: (id: string) => void;
    default: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => string;
    success: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => string;
    error: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => string;
    warning: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => string;
    info: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => string;
  };
}

const ToastContext = createContext<ToastContextType | null>(null);

// Sub-componente que maneja el ciclo de vida (animación y auto-cierre) de cada toast individualmente
const ToastMessage = ({ item, onClose }: { item: ToastItem; onClose: (id: string) => void }) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!item.duration) return;
    
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onClose(item.id), 300);
    }, item.duration);
    
    return () => clearTimeout(timer);
  }, [item.duration, item.id, onClose]);

  const handleClose = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setVisible(false);
    setTimeout(() => onClose(item.id), 300);
  };

  const handleClick = () => {
    if (item.onClick) {
      item.onClick();
    } else {
      handleClose();
    }
  };

  const getVariantConfig = (variant: ToastVariant) => {
    switch (variant) {
      case 'success': return { defaultIcon: <CheckCircleIcon color="success" />, color: theme.palette.success.main };
      case 'error': return { defaultIcon: <ErrorIcon color="error" />, color: theme.palette.error.main };
      case 'warning': return { defaultIcon: <WarningIcon color="warning" />, color: theme.palette.warning.main };
      case 'info': return { defaultIcon: <InfoIcon color="info" />, color: theme.palette.info.main };
      default: return { defaultIcon: null, color: theme.palette.mode === 'dark' ? 'grey.800' : 'rgba(0, 0, 0, 0.05)' };
    }
  };

  const { defaultIcon, color: variantColor } = getVariantConfig(item.variant || 'default');
  const displayIcon = item.icon || defaultIcon;

  return (
    <Slide in={visible} direction="left" unmountOnExit>
      <Box
        onClick={handleClick}
        sx={{
          maxWidth: 384,
          width: '100%',
          minWidth: 300,
          boxShadow: 4,
          borderRadius: 1,
          border: '1px solid',
          borderColor: item.variant === 'default' ? variantColor : theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
          borderLeft: item.variant !== 'default' ? `4px solid ${variantColor}` : undefined,
          p: 2,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
          color: theme.palette.mode === 'dark' ? 'common.white' : 'grey.900',
          pointerEvents: 'auto',
          display: 'flex',
          gap: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {displayIcon && (
          <Box sx={{ mb: 0.5, pt: 0.5, fontSize: '1.5rem', display: 'flex' }}>
            {displayIcon}
          </Box>
        )}

        <Box sx={{ width: '100%', minWidth: 0, flexGrow: 1 }}>
          {item.title && (
            <Typography variant="subtitle2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.title}
            </Typography>
          )}
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {item.message}
          </Typography>
        </Box>

        {item.showCloseButton !== false && (
          <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
            <IconButton color="inherit" aria-label="close toast" edge="end" size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Slide>
  );
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((props: ShowToastProps) => {
    const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setToasts((prev) => [...prev, { ...props, id }]);
    return id;
  }, []);

  const duration = 5000;
  const toastHelper = {
    dismiss: removeToast,
    default: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => showToast({ message, variant: 'default', duration, ...options }),
    success: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => showToast({ message, variant: 'success', duration, ...options }),
    error: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => showToast({ message, variant: 'error', duration, ...options }),
    warning: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => showToast({ message, variant: 'warning', duration, ...options }),
    info: (message: string, options?: Omit<ShowToastProps, 'message' | 'variant'>) => showToast({ message, variant: 'info', duration, ...options }),
  };

  return (
    <ToastContext.Provider value={{ showToast, toast: toastHelper }}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 2147483647,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            pointerEvents: 'none',
          }}
        >
          {toasts.map((t) => (
            <ToastMessage key={t.id} item={t} onClose={removeToast} />
          ))}
        </Box>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast debe ser usado dentro de un ToastProvider');
  return context;
};